import React, { useCallback, useRef } from 'react';
import { FormikProps } from 'formik';

import { generateId } from '@lyearn/core/utils/id';
import { useTranslation } from '@lyearn/i18n';
import { SparkleOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Button, Stack, Typography } from '@lyearn/ui';
import BaseForm, { FieldOverrides } from '@/components/Form';
import Loader from '@/components/Loader';
import { Modal } from '@/components/Modals';
import { ModalVariant } from '@/components/Modals/types';
import { PreserveModalForm } from '@/components/PreserveModalForm';
import { PeerFeedbackStatusEnum } from '@/types/schema';

import FeedbackTemplateSidebar from './components/FeedbackTemplateSidebar';
import useCreateFeedback from './hooks/useCreateFeedback';
import useFeedbackTemplate from './hooks/useFeedbackTemplate';
import { buttonWidth, createFeedbackModalStyles, sidebarWidth } from './styles';
import { CreateFeedbackDialogProps, InitialFormValueType } from './types';

const OVERRIDES: FieldOverrides = {
  Section: {
    spacing: 'space-y-40',
    Name: {
      variant: 'heading-xs',
    },
    Header: {
      className: 'mb-16 ms-8 space-y-8',
    },
  },
  spacing: {
    row: 'space-y-24',
  },
};

const CreateFeedbackModal: React.FC<CreateFeedbackDialogProps> = (props) => {
  const { initialFormValues } = props;
  const {
    submitRef,
    fetching,
    fields,
    sections,
    setSubmitRef,
    validate,
    onSubmit,
    onChange,
    onUpdate,
    selectedValues,
    setSelectedValues,
    onCloseModal,
    modalClosePrompt,
  } = useCreateFeedback(props);
  const { t } = useTranslation('Feedback');
  const formRef = useRef<FormikProps<any>>(null);
  const { fetching: feedbackFetching, feedbackTemplate } = useFeedbackTemplate();

  const setSelectedTemplate = useCallback(
    (textString: string) => {
      formRef.current?.setFieldValue('feedbackMessage', textString);
      // This will be used as a key to re-render the feedback message editor
      // after template selection.
      formRef.current?.setFieldValue('templateId', generateId());
      setSelectedValues({ ...selectedValues, feedbackMessage: textString });
    },
    [selectedValues, setSelectedValues],
  );

  return (
    <PreserveModalForm {...modalClosePrompt}>
      <Modal
        classes={{ paper: createFeedbackModalStyles }}
        showCloseBtn={true}
        variant={ModalVariant.ExtraWide}
        onClose={onCloseModal}>
        <Stack className="w-full overflow-y-auto">
          <Stack className="flex-1 overflow-y-auto" direction="col">
            <Stack className="sticky top-0 z-50 border-b border-divider bg-bg-primary px-24 pt-28 pb-28">
              <Typography className="heading-m m-0 flex ps-8 pe-48" variant="heading-m">
                {initialFormValues?.receiverUserId &&
                initialFormValues.status !== PeerFeedbackStatusEnum.Requested
                  ? t('Feedback:CreateFeedback.EditTitle')
                  : t('Feedback:CreateFeedback.Title')}
              </Typography>
            </Stack>
            <Stack className="z-10 overflow-y-auto px-32 pb-24 pt-16" direction="col">
              <BaseForm<InitialFormValueType>
                fields={fields}
                formName="SetupMeeting"
                formRef={formRef}
                initialValues={selectedValues}
                overrides={OVERRIDES}
                sectionLayout={sections}
                setSubmitRef={setSubmitRef}
                validate={validate}
                onChange={onChange}
                onSubmit={initialFormValues?.receiverUserId ? onUpdate : onSubmit}
              />
            </Stack>
            <Stack
              className="sticky bottom-0 z-50 flex-1 border-t border-divider bg-bg-primary px-20 py-16"
              justifyContent="end">
              <Stack gapX={8}>
                <Button
                  appearance="secondary"
                  className={buttonWidth}
                  size="large"
                  variant="outlined"
                  onClick={onCloseModal}>
                  {t('Common:Cancel')}
                </Button>
                <Button
                  appearance="primary"
                  className={buttonWidth}
                  disabled={fetching}
                  loading={fetching}
                  size="large"
                  onClick={submitRef}>
                  {t('Common:Share')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
          <Stack
            className={cn('overflow-auto border-divider px-20 pt-32 border-s', sidebarWidth)}
            direction="col">
            <Stack className="px-16" direction="col" gapY={16}>
              <Stack gapX={8}>
                <SparkleOutline className="h-20 w-20" color="icon-active" />
                <Typography variant="heading-xs">
                  {t('Feedback:CreateFeedback.FeedbackTemplateHeading')}
                </Typography>
              </Stack>
              <Typography
                className="whitespace-pre-line"
                color="text-text-paragraph"
                variant="body-short-02">
                {t('Feedback:CreateFeedback.FeedbackTemplateDescription')}
              </Typography>
            </Stack>
            {feedbackFetching ? (
              <Stack className="flex-1">
                <Loader />
              </Stack>
            ) : (
              <FeedbackTemplateSidebar
                feedbackTemplate={feedbackTemplate}
                setSelectedTemplate={setSelectedTemplate}
              />
            )}
          </Stack>
        </Stack>
      </Modal>
    </PreserveModalForm>
  );
};

export default CreateFeedbackModal;
